import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Eye,
  EyeOff,
  Gift,
  Loader2,
  Lock,
  Mail,
  Phone,
  User,
  Users,
  CheckCircle2,
  Clipboard,
  ClipboardCheck,
  AlertCircle,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { getApiErrorMessage } from "@/services/httpClient";
import { referralsService } from "@/services/referralsService";
import {
  formatIndianMobileDisplay,
  indianMobileHint,
  isValidIndianMobile,
  sanitizeIndianMobileInput,
  toIndianE164,
} from "@/lib/phone-in";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

type ReferralStatus = "idle" | "checking" | "valid" | "invalid";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralFromUrl = (searchParams.get("ref") ?? "").trim().toUpperCase();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [referralCode, setReferralCode] = useState(referralFromUrl);
  const [referralStatus, setReferralStatus] = useState<ReferralStatus>(
    referralFromUrl ? "checking" : "idle",
  );
  const [referralMessage, setReferralMessage] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { signup } = useAuth();
  const { toast } = useToast();
  const [successData, setSuccessData] = useState<{
    username: string;
    email: string;
    password: string;
  } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const phoneDisplay = formatIndianMobileDisplay(phoneDigits);
  const phoneHint = indianMobileHint(phoneDigits);
  const phoneValid = isValidIndianMobile(phoneDigits);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast({ title: "Copied!", description: `${field} copied to clipboard.` });
    setTimeout(() => setCopiedField(null), 2000);
  };

  const hasReferralInvite = useMemo(() => Boolean(referralFromUrl), [referralFromUrl]);

  // Debounced referral validation
  useEffect(() => {
    const code = referralCode.trim().toUpperCase();
    if (!code) {
      setReferralStatus("idle");
      setReferralMessage("");
      return;
    }

    setReferralStatus("checking");
    const timer = window.setTimeout(async () => {
      try {
        const result = await referralsService.validateCode(code);
        if (result.isValid) {
          setReferralStatus("valid");
          setReferralMessage(result.message || `Valid — sponsored by ${result.sponsorName ?? "member"}`);
        } else {
          setReferralStatus("invalid");
          setReferralMessage(
            result.message ||
              "Invalid referral code. Enter a correct code or leave blank to register normally.",
          );
        }
      } catch {
        setReferralStatus("invalid");
        setReferralMessage("Could not validate referral code. Leave blank to register without one.");
      }
    }, 450);

    return () => window.clearTimeout(timer);
  }, [referralCode]);

  const passwordStrength = useMemo(() => {
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 6) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  }, [password]);

  const strengthLabel =
    passwordStrength === 0
      ? ""
      : passwordStrength <= 25
        ? "Weak"
        : passwordStrength <= 50
          ? "Fair"
          : passwordStrength <= 75
            ? "Good"
            : "Strong";

  const canSubmit =
    !submitting &&
    acceptedTerms &&
    phoneValid &&
    password.length >= 6 &&
    password === confirmPassword &&
    (referralStatus === "idle" || referralStatus === "valid") &&
    referralStatus !== "checking";

  const handlePhoneChange = (raw: string) => {
    setPhoneDigits(sanitizeIndianMobileInput(raw));
  };

  const handleClearReferral = () => {
    setReferralCode("");
    setReferralStatus("idle");
    setReferralMessage("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "The password and confirmation password do not match.",
        variant: "destructive",
      });
      return;
    }
    if (!acceptedTerms) {
      toast({
        title: "Terms required",
        description: "Please accept the Terms of Service and Privacy Policy.",
        variant: "destructive",
      });
      return;
    }
    if (!phoneValid) {
      toast({
        title: "Invalid mobile number",
        description: phoneHint ?? "Enter a valid 10-digit Indian mobile number.",
        variant: "destructive",
      });
      return;
    }
    if (referralCode.trim() && referralStatus === "invalid") {
      toast({
        title: "Invalid referral code",
        description: "Enter a correct referral code, or clear the field to register without one.",
        variant: "destructive",
      });
      return;
    }
    if (referralCode.trim() && referralStatus === "checking") {
      toast({
        title: "Please wait",
        description: "Validating referral code…",
      });
      return;
    }

    const e164 = toIndianE164(phoneDigits);
    if (!e164) {
      toast({
        title: "Invalid mobile number",
        description: "Enter a valid Indian mobile number.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const registeredUser = await signup({
        name,
        email,
        phone: e164,
        password,
        referralCode: referralCode.trim() || undefined,
      });
      if (!registeredUser) {
        toast({
          title: "Registration failed",
          description: "Please check your details and try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Welcome to Bihari Mall!",
        description: `Your account has been created successfully. Your auto-generated username is ${registeredUser.username}. Please save it for future logins.`,
      });

      setSuccessData({
        username: registeredUser.username,
        email: registeredUser.email,
        password: password,
      });
    } catch (error) {
      toast({
        title: "Registration failed",
        description: getApiErrorMessage(error, "Please check your details and try again."),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {hasReferralInvite && (
        <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3">
          <Gift className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div>
            <p className="text-sm font-semibold text-foreground">Referral invite detected</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Joining with code{" "}
              <span className="font-mono font-semibold text-primary">{referralFromUrl}</span>
            </p>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-3 p-4 sm:p-5">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-sm font-medium">
              Full name
            </Label>
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="name"
                placeholder="Your full name"
                className="h-11 pl-9"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={submitting}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-sm font-medium">
              Email
            </Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                className="h-11 pl-9"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={submitting}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone" className="text-sm font-medium">
              Mobile number
            </Label>
            <div
              className={cn(
                "flex h-11 overflow-hidden rounded-md border border-input bg-background shadow-sm transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                phoneDigits && !phoneValid && "border-destructive focus-within:ring-destructive",
                phoneValid && "border-success focus-within:ring-success",
              )}
            >
              <div className="flex shrink-0 items-center gap-1.5 border-r border-border bg-muted/40 px-3 text-sm font-medium text-muted-foreground">
                <Phone className="h-3.5 w-3.5" />
                +91
              </div>
              <Input
                id="phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="98765 43210"
                className="h-full flex-1 border-0 bg-transparent pl-3 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                value={phoneDisplay}
                onChange={(e) => handlePhoneChange(e.target.value)}
                required
                disabled={submitting}
                maxLength={14}
                aria-invalid={Boolean(phoneDigits && !phoneValid)}
                aria-describedby="phone-hint"
              />
            </div>
            <p
              id="phone-hint"
              className={cn(
                "text-xs leading-snug",
                !phoneDigits && "text-muted-foreground",
                phoneDigits && phoneValid && "text-success",
                phoneDigits && !phoneValid && "text-destructive",
              )}
            >
              {!phoneDigits
                ? "10-digit Indian mobile (starts with 6–9)"
                : phoneValid
                  ? "Valid Indian mobile number"
                  : phoneHint}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="referral" className="text-sm font-medium">
              Referral code{" "}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <div className="relative">
              <Users className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="referral"
                placeholder="Leave blank if you don't have one"
                className={cn(
                  "h-11 pl-9 pr-10 tracking-wide placeholder:normal-case placeholder:tracking-normal",
                  referralCode && "uppercase",
                  referralStatus === "valid" && "border-success focus-visible:ring-success",
                  referralStatus === "invalid" && "border-destructive focus-visible:ring-destructive",
                )}
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/\s+/g, ""))}
                disabled={submitting}
                autoComplete="off"
                aria-invalid={referralStatus === "invalid"}
                aria-describedby="referral-hint"
              />
              {referralCode ? (
                <button
                  type="button"
                  onClick={handleClearReferral}
                  className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Clear referral code"
                  tabIndex={-1}
                >
                  <XCircle className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <div id="referral-hint">
              {referralStatus === "checking" && (
                <p className="flex items-center gap-1.5 text-xs leading-snug text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking referral code…
                </p>
              )}
              {referralStatus === "valid" && (
                <p className="flex items-center gap-1.5 text-xs leading-snug text-success">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> {referralMessage}
                </p>
              )}
              {referralStatus === "invalid" && (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/25 bg-destructive/5 px-2.5 py-2 text-xs text-destructive">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <div className="space-y-1">
                    <p>{referralMessage}</p>
                    <button
                      type="button"
                      className="font-medium text-destructive underline underline-offset-2 hover:no-underline"
                      onClick={handleClearReferral}
                    >
                      Clear code and register without referral
                    </button>
                  </div>
                </div>
              )}
              {referralStatus === "idle" && !referralCode && (
                <p className="text-xs leading-snug text-muted-foreground">
                  Optional — leave blank to register as a normal user.
                </p>
              )}
            </div>
          </div>

          <div className="grid items-start gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Min. 6 characters"
                  className="h-11 pl-9 pr-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirm password
              </Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Re-enter password"
                  className={cn(
                    "h-11 pl-9",
                    confirmPassword &&
                      password !== confirmPassword &&
                      "border-destructive focus-visible:ring-destructive",
                  )}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  disabled={submitting}
                />
              </div>
              {confirmPassword && password !== confirmPassword ? (
                <p className="text-xs leading-snug text-destructive">Passwords do not match</p>
              ) : null}
            </div>
          </div>

          {password ? (
            <div className="space-y-1">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${passwordStrength}%` }}
                  transition={{ duration: 0.25 }}
                  className={cn(
                    "h-full transition-colors",
                    passwordStrength <= 25 && "bg-destructive",
                    passwordStrength > 25 && passwordStrength <= 50 && "bg-warning",
                    passwordStrength > 50 && passwordStrength <= 75 && "bg-info",
                    passwordStrength > 75 && "bg-success",
                  )}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Strength: <span className="font-medium text-foreground">{strengthLabel}</span>
              </p>
            </div>
          ) : null}

          <div className="flex items-start gap-2.5">
            <Checkbox
              id="terms"
              checked={acceptedTerms}
              onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
              className="mt-0.5"
              disabled={submitting}
            />
            <Label
              htmlFor="terms"
              className="cursor-pointer text-xs font-normal leading-relaxed text-muted-foreground"
            >
              I agree to the{" "}
              <Link to="/terms" className="font-medium text-primary hover:underline">
                Terms of Service
              </Link>
              {" and "}
              <Link to="/privacy" className="font-medium text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </Label>
          </div>

          <Button
            type="submit"
            disabled={!canSubmit}
            className="h-11 w-full text-sm font-semibold disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating your account…
              </>
            ) : (
              "Create account"
            )}
          </Button>
        </form>
      </div>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link to="/auth/login" className="font-semibold text-primary hover:underline">
          Sign in
        </Link>
      </p>

      <Dialog open={Boolean(successData)} onOpenChange={() => {}}>
        <DialogContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          className="max-w-md border-border/80 bg-card rounded-2xl shadow-xl p-6 overflow-hidden [&>button]:hidden animate-in fade-in zoom-in-95 duration-200"
        >
          <DialogHeader className="flex flex-col items-center text-center space-y-3 pb-2">
            <div className="rounded-full bg-green-500/10 p-3 text-green-500 animate-bounce">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
            <DialogTitle className="text-2xl font-bold tracking-tight text-foreground">
              Registration Successful!
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground max-w-sm">
              Your account has been created. Please save these credentials before proceeding to login.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-3">
              <div className="flex items-center justify-between gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Username</p>
                  <p className="font-mono font-semibold text-foreground text-base mt-0.5">{successData?.username}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => successData && copyToClipboard(successData.username, "Username")}
                >
                  {copiedField === "Username" ? (
                    <ClipboardCheck className="h-4 w-4 text-green-500" />
                  ) : (
                    <Clipboard className="h-4 w-4" />
                  )}
                </Button>
              </div>

              <div className="border-t border-border/50" />

              <div className="flex items-center justify-between gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Email Address</p>
                  <p className="font-medium text-foreground text-sm mt-0.5">{successData?.email}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => successData && copyToClipboard(successData.email, "Email")}
                >
                  {copiedField === "Email" ? (
                    <ClipboardCheck className="h-4 w-4 text-green-500" />
                  ) : (
                    <Clipboard className="h-4 w-4" />
                  )}
                </Button>
              </div>

              <div className="border-t border-border/50" />

              <div className="flex items-center justify-between gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Password</p>
                  <p className="font-mono font-medium text-foreground text-sm mt-0.5">{successData?.password}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => successData && copyToClipboard(successData.password, "Password")}
                >
                  {copiedField === "Password" ? (
                    <ClipboardCheck className="h-4 w-4 text-green-500" />
                  ) : (
                    <Clipboard className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-3 text-xs text-blue-600 dark:text-blue-400">
              <p className="font-semibold mb-0.5">Please Note:</p>
              <p>For security, you must use your auto-generated Username (not your email) to log into the dashboard.</p>
            </div>
          </div>

          <DialogFooter className="w-full sm:flex-col gap-2">
            <Button
              type="button"
              className="w-full h-11 text-base font-medium rounded-xl shadow-md"
              onClick={() => {
                if (successData) {
                  navigate(`/auth/login?username=${encodeURIComponent(successData.username)}`, { replace: true });
                }
              }}
            >
              Proceed to Login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Register;
